import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Link,
  Check,
  X,
  Eye,
  RefreshCw,
  Database,
  ChevronDown,
  Trash2,
} from 'lucide-react';
import { toast, confirm as confirmModal } from '../ui/Toast';

const TransMemoryTab = ({ projectId, user }) => {
  const [linkedProjects, setLinkedProjects] = useState([]);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [recomputeLabel, setRecomputeLabel] = useState('');
  const [showLinkDropdown, setShowLinkDropdown] = useState(false);
  const [availableProjects, setAvailableProjects] = useState([]);
  const [loadingDropdown, setLoadingDropdown] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [expandedInsight, setExpandedInsight] = useState(null);

  const insightTypes = [
    { value: 'concept', label: 'Concept', color: '#8b5cf6' },
    { value: 'decision', label: 'Décision', color: '#3b82f6' },
    { value: 'pattern', label: 'Pattern', color: '#10b981' },
    { value: 'warning', label: 'Alerte', color: '#3b82f6' },
    { value: 'opportunity', label: 'Opportunité', color: '#f59e0b' },
  ];

  // Fetch linked projects and insights
  useEffect(() => {
    fetchLinkedProjects();
    fetchInsights();
  }, [projectId]);

  const fetchLinkedProjects = async () => {
    try {
      const response = await axios.get(
        `/api/projects/${projectId}/trans-memory`
      );
      setLinkedProjects(response.data.links || []);
    } catch (error) {
      console.error('Error fetching linked projects:', error);
    }
  };

  const fetchInsights = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `/api/projects/${projectId}/trans-memory/insights`
      );
      setInsights(response.data.insights || []);
    } catch (error) {
      console.error('Error fetching insights:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableProjects = async () => {
    try {
      setLoadingDropdown(true);
      const response = await axios.get('/api/projects');
      const projects = Array.isArray(response.data) ? response.data : (response.data.projects || []);
      const otherProjects = projects.filter(
        (p) => p.id !== projectId
      );
      setAvailableProjects(otherProjects);
    } catch (error) {
      console.error('Error fetching available projects:', error);
    } finally {
      setLoadingDropdown(false);
    }
  };

  const handleShowLinkDropdown = () => {
    setShowLinkDropdown(true);
    if (availableProjects.length === 0) {
      fetchAvailableProjects();
    }
  };

  const handleLinkProject = async (targetProjectId) => {
    try {
      await axios.post(`/api/projects/${projectId}/trans-memory/links`, {
        targetProjectId,
      });
      setShowLinkDropdown(false);
      fetchLinkedProjects();
      fetchInsights();
    } catch (error) {
      console.error('Error linking project:', error);
      toast.error('Erreur lors de la liaison du projet');
    }
  };

  const handleDeleteLink = async (linkId) => {
    if (!(await confirmModal('Êtes-vous sûr de vouloir délier ce projet ?'))) {
      return;
    }

    try {
      await axios.delete(
        `/api/projects/${projectId}/trans-memory/links/${linkId}`
      );
      fetchLinkedProjects();
      fetchInsights();
    } catch (error) {
      console.error('Error deleting link:', error);
      toast.error('Erreur lors de la suppression du lien');
    }
  };

  const handleApproveInsight = async (insightId) => {
    try {
      await axios.patch(
        `/api/projects/${projectId}/trans-memory/insights/${insightId}/approve`
      );
      fetchInsights();
    } catch (error) {
      console.error('Error approving insight:', error);
      toast.error('Erreur lors de l\'approbation');
    }
  };

  const handleDismissInsight = async (insightId) => {
    try {
      await axios.patch(`/api/projects/${projectId}/trans-memory/insights/${insightId}/dismiss`);
      fetchInsights();
    } catch (error) {
      console.error('Error dismissing insight:', error);
      toast.error('Erreur lors du rejet');
    }
  };

  const handleResetInsight = async (insightId) => {
    try {
      await axios.patch(`/api/projects/${projectId}/trans-memory/insights/${insightId}/reset`);
      fetchInsights();
    } catch (error) {
      console.error('Error resetting insight:', error);
      toast.error('Erreur lors de la réinitialisation');
    }
  };

  const handleForgetInsight = async (insight) => {
    const confirmed = await confirmModal(
      `Oublier définitivement ce rappel trans-projet ?\n\n« ${insight.title || insight.content?.slice(0, 80)} »\n\nCet insight ne remontera plus sur ce projet. L'action est irréversible côté interface (audit conservé en base).`,
      { confirmText: 'Oublier', danger: true }
    );
    if (!confirmed) return;
    try {
      await axios.delete(
        `/api/projects/${projectId}/trans-memory/insights/${insight.id}/forget`
      );
      toast.success('Rappel oublié');
      setInsights(prev => prev.filter(i => i.id !== insight.id));
    } catch (error) {
      console.error('Error forgetting insight:', error);
      toast.error('Erreur lors de l\'oubli');
    }
  };

  // Libellé lisible pour chaque étape émise par le backend.
  const libelleEtape = (e) => {
    switch (e.phase) {
      case 'debut':        return e.total ? `${e.total} lien(s) à analyser…` : 'Analyse…';
      case 'lien':         return `${e.label} (${e.index}/${e.total})…`;
      case 'lien_ignore':  return `${e.label} — ignoré, ${e.raison}`;
      case 'lien_termine': return `${e.label} — ${e.insights} insight(s)`;
      case 'fin':          return 'Terminé';
      default:             return 'Analyse…';
    }
  };

  // Le recalcul arrive en flux SSE : on suit la progression lien par lien au lieu
  // d'un bouton muet. fetch et non axios, qui ne sait pas lire un flux.
  const handleRecompute = async () => {
    setRecomputing(true);
    setRecomputeLabel('Démarrage…');

    try {
      const response = await fetch(`/api/projects/${projectId}/trans-memory/recompute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: axios.defaults.headers.common['Authorization'],
        },
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let bilan = null;
      let erreur = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let idx;
        while ((idx = buffer.indexOf('\n\n')) !== -1) {
          const brut = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          if (!brut.startsWith('data: ')) continue;

          const payload = brut.slice(6);
          if (payload === '[DONE]') continue;

          let data;
          try { data = JSON.parse(payload); } catch { continue; }

          if (data.type === 'status') setRecomputeLabel(libelleEtape(data));
          else if (data.type === 'done') bilan = data;
          else if (data.type === 'error') erreur = data;
        }
      }

      if (erreur) {
        toast.error(erreur.message || 'Erreur lors du recalcul');
      } else if (bilan) {
        if (bilan.liens === 0) {
          toast.info('Aucun projet lié à analyser.');
        } else if (bilan.insights === 0) {
          toast.info(`${bilan.liens} lien(s) analysé(s) — aucun rapprochement trouvé.`);
        } else {
          toast.success(`${bilan.insights} insight(s) généré(s) sur ${bilan.liens} lien(s).`);
        }
      }

      await fetchInsights();
    } catch (error) {
      console.error('Error recomputing trans-memory:', error);
      toast.error('Erreur lors du recalcul');
    } finally {
      setRecomputing(false);
      setRecomputeLabel('');
    }
  };

  const getScoreColor = (score) => {
    if (score >= 0.85) return '#10b981';
    if (score >= 0.75) return '#eab308';
    return '#6b7280';
  };

  const getTypeColor = (type) => {
    const t = insightTypes.find((it) => it.value === type);
    return t ? t.color : '#6b7280';
  };

  const getTypeLabel = (type) => {
    const t = insightTypes.find((it) => it.value === type);
    return t ? t.label : type;
  };

  const filteredInsights = insights.filter((insight) => {
    if (filterType !== 'all' && insight.type !== filterType) return false;
    if (filterStatus !== 'all' && insight.status !== filterStatus) return false;
    return true;
  });

  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: '24px',
      padding: '0',
      color: 'var(--text)',
    },
    linkedProjectsSection: {
      borderBottom: `1px solid var(--topbar-border)`,
      paddingBottom: '24px',
    },
    sectionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '16px',
    },
    sectionTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '16px',
      fontWeight: '600',
    },
    insightsSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
    },
    filterBar: {
      display: 'flex',
      gap: '12px',
      marginBottom: '16px',
      alignItems: 'center',
      flexWrap: 'wrap',
    },
    filterSelect: {
      padding: '6px 10px',
      backgroundColor: 'var(--card-bg)',
      color: 'var(--text)',
      border: `1px solid var(--topbar-border)`,
      borderRadius: '4px',
      fontSize: '13px',
      cursor: 'pointer',
    },
    button: {
      padding: '8px 16px',
      backgroundColor: '#3b82f6',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      fontSize: '13px',
      fontWeight: '500',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      transition: 'background-color 0.2s',
    },
    buttonSecondary: {
      padding: '8px 16px',
      backgroundColor: 'var(--card-bg)',
      color: 'var(--text)',
      border: `1px solid var(--topbar-border)`,
      borderRadius: '4px',
      fontSize: '13px',
      fontWeight: '500',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      transition: 'all 0.2s',
    },
    linkedProjectCard: {
      padding: '12px',
      backgroundColor: 'var(--card-bg)',
      border: `1px solid var(--topbar-border)`,
      borderRadius: '6px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '12px',
    },
    projectInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      flex: 1,
    },
    projectName: {
      fontSize: '14px',
      fontWeight: '500',
    },
    linkModeBadge: {
      padding: '2px 8px',
      backgroundColor: '#8b5cf6',
      color: 'white',
      borderRadius: '3px',
      fontSize: '11px',
      fontWeight: '500',
    },
    insightsCount: {
      padding: '2px 8px',
      backgroundColor: 'var(--topbar-border)',
      color: 'var(--text)',
      borderRadius: '3px',
      fontSize: '11px',
    },
    deleteButton: {
      padding: '4px 8px',
      backgroundColor: 'transparent',
      color: '#3b82f6',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      fontSize: '13px',
      transition: 'background-color 0.2s',
    },
    emptyState: {
      textAlign: 'center',
      padding: '48px 24px',
      color: 'var(--text)',
    },
    emptyIcon: {
      marginBottom: '16px',
      opacity: 0.5,
    },
    emptyTitle: {
      fontSize: '16px',
      fontWeight: '600',
      marginBottom: '8px',
    },
    emptyText: {
      fontSize: '13px',
      opacity: 0.7,
      marginBottom: '16px',
    },
    insightCard: {
      padding: '16px',
      backgroundColor: 'var(--card-bg)',
      border: `1px solid var(--topbar-border)`,
      borderRadius: '6px',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
    },
    insightHeader: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
    },
    scoreCircle: {
      width: '32px',
      height: '32px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontSize: '12px',
      fontWeight: '600',
      flexShrink: 0,
    },
    insightMeta: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      flexWrap: 'wrap',
    },
    insightTypeBadge: {
      padding: '2px 8px',
      borderRadius: '3px',
      color: 'white',
      fontSize: '11px',
      fontWeight: '500',
    },
    insightTitle: {
      fontSize: '14px',
      fontWeight: '600',
      lineHeight: '1.4',
    },
    insightText: {
      fontSize: '13px',
      lineHeight: '1.5',
      opacity: 0.85,
    },
    insightSource: {
      fontSize: '12px',
      opacity: 0.6,
      marginTop: '8px',
    },
    evidenceSection: {
      marginTop: '12px',
      padding: '12px',
      backgroundColor: 'rgba(139, 92, 246, 0.05)',
      borderRadius: '4px',
      fontSize: '12px',
      lineHeight: '1.5',
    },
    evidenceTitle: {
      fontWeight: '600',
      marginBottom: '8px',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      cursor: 'pointer',
    },
    insightActions: {
      display: 'flex',
      gap: '8px',
      justifyContent: 'flex-start',
      marginTop: '8px',
    },
    actionButton: {
      padding: '6px 10px',
      backgroundColor: 'transparent',
      border: `1px solid var(--topbar-border)`,
      borderRadius: '4px',
      color: 'var(--text)',
      fontSize: '12px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      transition: 'all 0.2s',
    },
    dropdownMenu: {
      position: 'absolute',
      backgroundColor: 'var(--card-bg)',
      border: `1px solid var(--topbar-border)`,
      borderRadius: '6px',
      marginTop: '4px',
      maxHeight: '300px',
      overflowY: 'auto',
      zIndex: 100,
      minWidth: '200px',
    },
    dropdownItem: {
      padding: '8px 12px',
      borderBottom: `1px solid var(--topbar-border)`,
      cursor: 'pointer',
      fontSize: '13px',
      transition: 'background-color 0.2s',
    },
    linkedProjectsGrid: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    topBarActions: {
      display: 'flex',
      gap: '8px',
    },
  };

  return (
    <div style={styles.container}>
      {/* Linked Projects Section */}
      <div style={styles.linkedProjectsSection}>
        <div style={styles.sectionHeader}>
          <div style={styles.sectionTitle}>
            <Link size={18} />
            Projets liés
          </div>
          <div style={styles.topBarActions}>
            <button
              style={styles.button}
              onClick={handleShowLinkDropdown}
            >
              + Lier un projet
            </button>
            {showLinkDropdown && (
              <div style={styles.dropdownMenu}>
                {loadingDropdown ? (
                  <div style={{ padding: '12px', textAlign: 'center', fontSize: '13px' }}>
                    Chargement...
                  </div>
                ) : availableProjects.length === 0 ? (
                  <div style={{ padding: '12px', textAlign: 'center', fontSize: '13px', opacity: 0.6 }}>
                    Aucun autre projet
                  </div>
                ) : (
                  availableProjects.map((proj) => (
                    <div
                      key={proj.id}
                      style={styles.dropdownItem}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = 'rgba(139, 92, 246, 0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                      }}
                      onClick={() => handleLinkProject(proj.id)}
                    >
                      {proj.name}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        {linkedProjects.length === 0 ? (
          <div style={{ ...styles.emptyState, padding: '24px' }}>
            <div style={{ ...styles.emptyText, opacity: 0.6 }}>
              Aucun projet lié. Créez une liaison pour commencer.
            </div>
          </div>
        ) : (
          <div style={styles.linkedProjectsGrid}>
            {linkedProjects.map((link) => (
              <div key={link.linkId} style={styles.linkedProjectCard}>
                <div style={styles.projectInfo}>
                  <div style={styles.projectName}>{link.project?.name || 'Projet inconnu'}</div>
                  <div
                    style={{
                      ...styles.linkModeBadge,
                      backgroundColor: link.linkMode === 'inject' ? '#3b82f6' : '#8b5cf6',
                    }}
                  >
                    {link.linkMode === 'inject' ? 'Injecter' : 'Suggérer'}
                  </div>
                  <div style={styles.insightsCount}>
                    {link.insightCount || 0} insights
                  </div>
                </div>
                <button
                  style={styles.deleteButton}
                  onClick={() => handleDeleteLink(link.linkId)}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'rgba(96, 165, 250, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                  }}
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Insights Section */}
      <div style={styles.insightsSection}>
        <div style={styles.sectionHeader}>
          <div style={styles.sectionTitle}>
            <Database size={18} />
            Insights découverts
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {recomputing && recomputeLabel && (
              <span style={{ fontSize: '13px', color: '#9ca3af' }}>{recomputeLabel}</span>
            )}
            <button
              style={{ ...styles.button, backgroundColor: recomputing ? '#9ca3af' : '#3b82f6' }}
              onClick={handleRecompute}
              disabled={recomputing}
            >
              <RefreshCw size={16} />
              {recomputing ? 'Recalcul...' : 'Recalculer'}
            </button>
          </div>
        </div>

        {linkedProjects.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>
              <Database size={40} />
            </div>
            <div style={styles.emptyTitle}>Mémoire trans-projet</div>
            <div style={styles.emptyText}>
              Liez vos projets pour découvrir des connexions entre eux.
            </div>
          </div>
        ) : insights.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyText}>
              Les insights seront générés automatiquement lors des prochains résumés.
            </div>
          </div>
        ) : (
          <>
            <div style={styles.filterBar}>
              <select
                style={styles.filterSelect}
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="all">Tous les types</option>
                {insightTypes.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
              <select
                style={styles.filterSelect}
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">Tous les statuts</option>
                <option value="pending">En attente</option>
                <option value="approved">Approuvé</option>
                <option value="dismissed">Rejeté</option>
              </select>
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '24px', opacity: 0.6 }}>
                Chargement des insights...
              </div>
            ) : filteredInsights.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px', opacity: 0.6 }}>
                Aucun insight correspondant aux critères.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {filteredInsights.map((insight) => (
                  <div key={insight.id} style={styles.insightCard}>
                    <div style={styles.insightHeader}>
                      <div
                        style={{
                          ...styles.scoreCircle,
                          backgroundColor: getScoreColor(insight.score),
                        }}
                      >
                        {(insight.score * 100).toFixed(0)}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={styles.insightMeta}>
                          <div
                            style={{
                              ...styles.insightTypeBadge,
                              backgroundColor: getTypeColor(insight.type),
                            }}
                          >
                            {getTypeLabel(insight.type)}
                          </div>
                          <span style={{ fontSize: '12px', opacity: 0.6 }}>
                            {insight.status === 'pending' && 'En attente'}
                            {insight.status === 'approved' && 'Approuvé'}
                            {insight.status === 'dismissed' && 'Rejeté'}
                          </span>
                        </div>
                        <div style={styles.insightTitle}>{insight.title}</div>
                        <div style={styles.insightText}>{insight.content}</div>
                        <div style={styles.insightSource}>
                          De <strong>{insight.sourceProject.name}</strong> •{' '}
                          {new Date(insight.createdAt).toLocaleDateString('fr-FR')}
                        </div>
                      </div>
                    </div>

                    {insight.evidence && (
                      <div style={styles.evidenceSection}>
                        <div
                          style={styles.evidenceTitle}
                          onClick={() =>
                            setExpandedInsight(
                              expandedInsight === insight.id ? null : insight.id
                            )
                          }
                        >
                          <Eye size={14} />
                          Preuves ({insight.evidence.length})
                          <ChevronDown
                            size={14}
                            style={{
                              transform:
                                expandedInsight === insight.id
                                  ? 'rotate(180deg)'
                                  : 'rotate(0deg)',
                              transition: 'transform 0.2s',
                            }}
                          />
                        </div>
                        {expandedInsight === insight.id && (
                          <div style={{ marginTop: '8px', opacity: 0.8 }}>
                            {insight.evidence.join(' • ')}
                          </div>
                        )}
                      </div>
                    )}

                    <div style={styles.insightActions}>
                      {insight.status === 'pending' && (
                        <>
                          <button
                            style={{
                              ...styles.actionButton,
                              borderColor: '#10b981',
                              color: '#10b981',
                            }}
                            onClick={() => handleApproveInsight(insight.id)}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = 'rgba(16, 185, 129, 0.1)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = 'transparent';
                            }}
                          >
                            <Check size={14} />
                            Approuver
                          </button>
                          <button
                            style={{
                              ...styles.actionButton,
                              borderColor: '#3b82f6',
                              color: '#3b82f6',
                            }}
                            onClick={() => handleDismissInsight(insight.id)}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = 'rgba(96, 165, 250, 0.1)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = 'transparent';
                            }}
                          >
                            <X size={14} />
                            Rejeter
                          </button>
                        </>
                      )}
                      {insight.status === 'approved' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#10b981', fontSize: '12px' }}>
                            <Check size={14} /> Approuvé
                          </div>
                          <button
                            onClick={() => handleResetInsight(insight.id)}
                            style={{ ...styles.actionButton, borderColor: '#6b7280', color: '#6b7280', fontSize: '11px', padding: '3px 8px' }}
                          >
                            Révoquer
                          </button>
                        </div>
                      )}
                      {insight.status === 'dismissed' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#6b7280', fontSize: '12px' }}>
                            <X size={14} /> Rejeté
                          </div>
                          <button
                            onClick={() => handleResetInsight(insight.id)}
                            style={{ ...styles.actionButton, borderColor: '#6b7280', color: '#6b7280', fontSize: '11px', padding: '3px 8px' }}
                          >
                            Révoquer
                          </button>
                        </div>
                      )}

                      {/* S45 P2 — Oubli chirurgical (irréversible) */}
                      <button
                        onClick={() => handleForgetInsight(insight)}
                        style={{
                          ...styles.actionButton,
                          marginLeft: 'auto',
                          borderColor: '#dc2626',
                          color: '#dc2626',
                          fontSize: '11px',
                          padding: '3px 8px',
                        }}
                        title="Oublier définitivement ce rappel trans-projet sur ce projet (irréversible)"
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(220, 38, 38, 0.1)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                      >
                        <Trash2 size={12} />
                        Oublier
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TransMemoryTab;
